import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock, X, FileText, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [intakeStatus, setIntakeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false, appointment: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, intakeRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/intake-form/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAppointments(appointmentsRes.data);
      setIntakeStatus(intakeRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    try {
      await axios.delete(`${API}/appointments/${cancelDialog.appointment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setCancelDialog({ open: false, appointment: null });
    }
  };

  const downloadInvoice = async (appointmentId) => {
    try {
      const response = await axios.get(`${API}/payments/invoice/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${appointmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: 'bg-green-100 text-green-700',
      pending_payment: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return variants[status] || 'bg-gray-100 text-gray-700';
  };

  const upcomingAppointments = appointments.filter(a => 
    new Date(a.start_time) > new Date() && a.status !== 'cancelled'
  );
  const pastAppointments = appointments.filter(a => 
    new Date(a.start_time) <= new Date() || a.status === 'cancelled'
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="dashboard-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-light mb-2">
              Welcome, {user?.first_name}
            </h1>
            <p className="text-muted-foreground">Manage your appointments and documents</p>
          </div>

          {/* Intake Form Reminder */}
          {!intakeStatus?.completed && (
            <div className="bg-accent/10 border border-accent/30 rounded-2xl p-6 mb-8">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <FileText className="h-6 w-6 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="font-medium mb-1">Complete Your Intake Form</h3>
                    <p className="text-sm text-muted-foreground">
                      Please complete the intake form before your first session. 
                      This helps us understand your needs better.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/intake-form')}
                  className="rounded-full"
                  data-testid="complete-intake-btn"
                >
                  Complete Form
                </Button>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-4 mb-8">
            <Button
              onClick={() => navigate('/book')}
              className="rounded-full"
              data-testid="book-new-btn"
            >
              Book New Appointment
            </Button>
          </div>

          {/* Upcoming Appointments */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-light mb-6">Upcoming Appointments</h2>
            {upcomingAppointments.length === 0 ? (
              <div className="bg-card rounded-2xl p-8 text-center shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No upcoming appointments</p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/book')}
                  className="rounded-full"
                >
                  Book Your First Session
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="bg-card rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-heading text-xl mb-2">{appointment.service_name}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {format(parseISO(appointment.start_time), 'EEEE, d MMMM yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {format(parseISO(appointment.start_time), 'HH:mm')} - {format(parseISO(appointment.end_time), 'HH:mm')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusBadge(appointment.status)}>
                          {appointment.status.replace('_', ' ')}
                        </Badge>
                        {appointment.payment_status === 'paid' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(appointment.id)}
                            data-testid={`download-invoice-${appointment.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Invoice
                          </Button>
                        )}
                        {appointment.status !== 'cancelled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCancelDialog({ open: true, appointment })}
                            className="text-destructive hover:text-destructive"
                            data-testid={`cancel-btn-${appointment.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Past Appointments */}
          {pastAppointments.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl font-light mb-6">Past Appointments</h2>
              <div className="space-y-4">
                {pastAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="bg-card rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] opacity-75"
                    data-testid={`past-appointment-${appointment.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-heading text-xl mb-2">{appointment.service_name}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {format(parseISO(appointment.start_time), 'EEEE, d MMMM yyyy')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusBadge(appointment.status)}>
                          {appointment.status.replace('_', ' ')}
                        </Badge>
                        {appointment.payment_status === 'paid' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(appointment.id)}
                            data-testid={`download-invoice-${appointment.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Invoice
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? Cancellations made less than 
              24 hours before the scheduled time may be subject to a fee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelAppointment} className="bg-destructive">
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardPage;
